<template>
  <div class="rank-custom-wrapper">
    <OrdersMapsFilters :default="filters" @update="handleFilters" />

    <div class="card card-inverse card-info text-center" v-if="loading">
      <div class="card-block">
        <blockquote class="card-blockquote">Chargement en cours ...</blockquote>
      </div>
    </div>
    <div class="card text-center" v-else-if="orders.length === 0">
      <div class="card-block">
        <blockquote class="card-blockquote">
          Aucune donnée, appliquer ou modifier les filtres
        </blockquote>
      </div>
    </div>

    <div v-else>
      <Tabs v-model="activeTab">
        <Tab header="Départements">
          <OrdersMapDepartments
            :ordersSortedByDepartment="mapData"
            :summary="summaryData"
            :ordersQuantity="orders.length"
          />
        </Tab>

        <Tab header="Global">
          <OrdersMapGlobal
            :clients="globalMapData"
            :summary="summaryData"
            :ordersQuantity="orders.length"
          />
        </Tab>
      </Tabs>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import Tabs from "vue-strap/src/Tabs";
import Tab from "vue-strap/src/Tab";

import OrdersMapDepartments from "@/components/order/OrdersMapDepartments.vue";
import OrdersMapGlobal from "@/components/order/OrdersMapGlobal.vue";
import OrdersMapsFilters from "@/components/order/OrdersMapsFilters.vue";

export default {
  components: {
    Tabs,
    Tab,
    OrdersMapDepartments,
    OrdersMapGlobal,
    OrdersMapsFilters,
  },

  data() {
    return {
      loading: false,
      orders: [],
      summaryData: null,
      activeTab: 0,
      filters: {
        begin: moment().startOf("year").toDate(),
        end: moment().toDate(),
        merchantId: undefined,
        concept: undefined,
        menuStats: undefined,
      },
    };
  },

  computed: {
    ...mapGetters({
      departments: "getDepartments",
    }),

    zipcodes() {
      return this.departments.map(({ code, name }) => {
        if (code === "all") {
          return { label: name, value: code };
        } else {
          return { label: code + " - " + name, value: code };
        }
      });
    },

    mapData() {
      const domTom = ["971", "972", "973", "974", "976"];
      const departments = [...this.zipcodes].filter(
        (dpt) => !domTom.includes(dpt.value) && dpt.value !== "all",
      );

      departments.forEach((dpt) => {
        dpt.data = this.departmentOrderInfo(dpt.value);

        dpt.totalTurnoverPayed = dpt.data
          .filter((order) => order.status === "payed")
          .reduce((acc, order) => {
            return acc + parseInt(order.price, 10);
          }, 0);
        dpt.totalTurnoverPartial = dpt.data
          .filter((order) => order.status === "partial")
          .reduce((acc, order) => {
            return acc + parseInt(order.price, 10);
          }, 0);
        dpt.totalRefunded = dpt.data.reduce((acc, order) => {
          const orderRefunded = order.refunds
            ? order.refunds.reduce(
                (refundAcc, refund) => refundAcc + parseInt(refund.price, 10),
                0,
              )
            : 0;

          return acc + orderRefunded;
        }, 0);
        dpt.totalTurnover =
          dpt.totalTurnoverPayed + dpt.totalTurnoverPartial - dpt.totalRefunded;
        dpt.totalComp = dpt.data.filter(
          (order) => order.merchantId === this.$store.state.merchantIds.company,
        ).length;
        dpt.totalCorp = dpt.data.filter(
          (order) =>
            order.merchantId === this.$store.state.merchantIds.corporate,
        ).length;
        dpt.totalB2B = dpt.data.filter((order) => order.isB2B).length;
        dpt.totalB2C = dpt.data.filter((order) => !order.isB2B).length;
        dpt.ratioPercentage =
          dpt.totalTurnover && dpt.totalTurnover > 0
            ? (dpt.totalTurnover /
                parseInt(this.summaryData.totalTurnover, 10)) *
              100
            : 0;
      });

      return departments;
    },

    globalMapData() {
      return this.orders
        .reduce((acc, order) => {
          const merchantCompany = this.$store.state.merchants.find(
            (m) => m.id === this.$store.state.merchantIds.company,
          );
          const merchantCorporate = this.$store.state.merchants.find(
            (m) => m.id === this.$store.state.merchantIds.corporate,
          );

          let index = acc.findIndex(
            (client) => client.id === order.refClient.id,
          );

          if (index === -1) {
            const newClient = {
              ...order.refClient,
              isB2B: order.isB2B,
              position: order.geoloc
                ? {
                    lat: order.geoloc.lat,
                    lng: order.geoloc.lng,
                  }
                : undefined,
              totalTurnover: 0,
              ordersQuantity: 0,
              merchantCompany: undefined,
              merchantCorporate: undefined,
              concepts: [],
            };

            acc.push(newClient);

            index = acc.findIndex((client) => client.id === order.refClient.id);
          }

          acc[index].totalTurnover += order.totalTurnover;
          acc[index].ordersQuantity++;
          acc[index].merchantCompany =
            order.merchantId === merchantCompany?.id
              ? merchantCompany.name
              : acc[index].merchantCompany;
          acc[index].merchantCorporate =
            order.merchantId === merchantCorporate?.id
              ? merchantCorporate.name
              : acc[index].merchantCorporate;

          if (order.concept) {
            const conceptIndex = acc[index].concepts.findIndex(
              (c) => c === order.concept,
            );

            if (conceptIndex === -1) {
              acc[index].concepts.push(order.concept);
            }
          }

          return acc;
        }, [])
        .filter((c) => typeof c.position.lat === "number");
    },
  },

  methods: {
    handleFilters(newFilters) {
      this.filters = newFilters;

      this.fetchOrdersData();
    },

    async fetchOrdersData() {
      try {
        this.loading = true;

        const { data } = await this.$api.get("/orders/maps", {
          params: {
            begin: this.filters.begin,
            end: this.filters.end,
            merchantId: this.filters.merchantId,
            concept: this.filters.concept,
            menuStats: this.filters.menuStats,
          },
        });

        this.orders = data.orders;
        this.summaryData = data.summary;
        /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
      } catch (e) {
      } finally {
        this.loading = false;
      }
    },

    departmentOrderInfo(departmentCode) {
      return this.orders.filter((order) => {
        if (order.department && typeof order.department === "string") {
          return order.department === departmentCode;
        }
      });
    },
  },
};
</script>
